import React, { useState } from 'react';
import { Box, Card, CardContent, Grid, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Avatar, Stack, LinearProgress, Divider, Tooltip, Typography, Accordion, AccordionSummary, AccordionDetails, List, ListItem, ListItemText, MenuItem, Checkbox, FormControlLabel } from '@mui/material';
import { Add, Edit, Delete, Visibility, TrendingUp, SwapHoriz, Loyalty, Insights } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useThemeMode } from '../context/ThemeContext';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPartners, createPartner, updatePartner, deletePartner, setSelectedPartner } from '../store/slices/partnersSlice';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-toastify';

const validationSchema = yup.object({
  businessName: yup.string('Enter business name').required('Business name is required'),
  email: yup.string('Enter email').email('Invalid email').required('Email is required'),
  phone: yup.string('Enter phone').required('Phone is required')
});

function Sparkline({ data }) {
  const width = 120;
  const height = 36;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((d - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <polyline
        fill="none"
        stroke="#1976d2"
        strokeWidth="2"
        points={points}
      />
    </svg>
  );
}

function Timeline({ items }) {
  return (
    <Box sx={{ position: 'relative', pl: 3 }}>
      <Box sx={{ position: 'absolute', left: 10, top: 0, bottom: 0, width: 2, bgcolor: '#e0e0e0' }} />
      <Stack spacing={2}>
        {items.map((item, idx) => (
          <Box key={`${item.date}-${idx}`} sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#1976d2', mt: 0.5 }} />
            <Box>
              <Typography variant="caption" color="textSecondary">{item.date}</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{item.title}</Typography>
              <Typography variant="body2" color="textSecondary">{item.detail}</Typography>
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

export default function PartnersPage() {
  const dispatch = useDispatch();
  const { list, pagination, loading, error } = useSelector(state => state.partners);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');
  const [partnerType, setPartnerType] = useState('all');
  const [viewOpen, setViewOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [activePartner, setActivePartner] = useState(null);
  const [assignedRewards, setAssignedRewards] = useState({});
  const [rewardSelection, setRewardSelection] = useState([]);
  const [timelineType, setTimelineType] = useState('all');
  const [timelineRange, setTimelineRange] = useState('90d');
  const { isDark, colors } = useThemeMode();
  
  // Theme-aware colors
  const themeColors = {
    cardBg: isDark ? '#1e293b' : '#ffffff',
    cardBorder: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    textPrimary: isDark ? '#f1f5f9' : '#1e293b',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    chipBg: isDark ? 'rgba(29,202,255,0.2)' : 'rgba(29,202,255,0.1)',
    tableBg: isDark ? '#0f172a' : '#f8fafc',
  };

  React.useEffect(() => {
    dispatch(fetchPartners());
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      businessName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      country: ''
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        if (editingId) {
          await dispatch(updatePartner({ id: editingId, data: values }));
          toast.success('Marca actualizada exitosamente');
        } else {
          await dispatch(createPartner(values));
          toast.success('Marca creada exitosamente');
        }
        handleCloseDialog();
        dispatch(fetchPartners());
      } catch (error) {
        toast.error('Error al guardar la marca');
      }
    }
  });

  const handleOpenDialog = () => {
    setEditingId(null);
    formik.resetForm();
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const handleEdit = (partner) => {
    setEditingId(partner.id);
    formik.setValues({
      businessName: partner.business_name,
      email: partner.email,
      phone: partner.phone,
      address: partner.address || '',
      city: partner.city || '',
      country: partner.country || ''
    });
    setOpenDialog(true);
  };

  const handleView = (partner) => {
    setActivePartner(partner);
    setViewOpen(true);
  };

  const handleOpenAssign = (partner) => {
    const existing = assignedRewards[partner.id] || partner.sharedRewards || [];
    setRewardSelection(existing);
    setActivePartner(partner);
    setAssignOpen(true);
  };

  const handleSaveAssign = () => {
    if (!activePartner) return;
    setAssignedRewards((prev) => ({
      ...prev,
      [activePartner.id]: rewardSelection
    }));
    setAssignOpen(false);
  };

  const handleToggleReward = (reward) => {
    setRewardSelection((prev) => {
      if (prev.includes(reward)) return prev.filter(r => r !== reward);
      return [...prev, reward];
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Está seguro de que desea eliminar esta marca?')) {
      try {
        await dispatch(deletePartner(id));
        toast.success('Marca eliminada');
        dispatch(fetchPartners());
      } catch (error) {
        toast.error('Error al eliminar la marca');
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      ACTIVE: 'success',
      INACTIVE: 'default',
      SUSPENDED: 'error'
    };
    return colors[status] || 'default';
  };

  const allianceMetricsByName = {
    'Grandvalira': {
      sharedRewards: ['Après‑Ski Relax', 'Ski & Spa Combo', 'VIP Thermal Pass'],
      crossSellRate: 18,
      externalAdoption: 22,
      effectivenessScore: 84,
      strategy: 'Bundle ski + spa en ventana 16:30–18:00 con SmartTiming™.',
      changeHistory: [
        { date: '2026-01-28', action: 'Actualización de rewards', detail: 'Se agregó VIP Thermal Pass' },
        { date: '2026-01-12', action: 'Ajuste de estrategia', detail: 'Nueva ventana 16:30–18:00' },
        { date: '2025-12-22', action: 'Campaña conjunta', detail: 'Lanzamiento Ski & Spa Combo' }
      ],
      activityLog: [
        { date: '2026-02-03', event: 'Redención', detail: 'Après‑Ski Relax · 126 usos' },
        { date: '2026-02-01', event: 'Cross‑sell', detail: 'Ski & Spa Combo · +4.2% lift' },
        { date: '2026-01-30', event: 'Mensaje PEpL', detail: 'CTR 9.1% en segmento premium' }
      ]
    },
    'Caldea': {
      sharedRewards: ['Thermal Premium', 'Family Pack', 'Relax Night'],
      crossSellRate: 12,
      externalAdoption: 15,
      effectivenessScore: 79,
      strategy: 'Promos por cohortes familiares + upsell nocturno.',
      changeHistory: [
        { date: '2026-01-25', action: 'Optimización catálogo', detail: 'Family Pack renovado' },
        { date: '2026-01-10', action: 'Segmentación', detail: 'Cohortes familiares activadas' }
      ],
      activityLog: [
        { date: '2026-02-02', event: 'Redención', detail: 'Thermal Premium · 214 usos' },
        { date: '2026-01-29', event: 'Upsell', detail: 'Relax Night · +3.6% ticket medio' }
      ]
    },
    'Andorra la Vella Hotels': {
      sharedRewards: ['Hotel + Spa', 'Late Check‑out Wellness'],
      crossSellRate: 21,
      externalAdoption: 27,
      effectivenessScore: 88,
      strategy: 'Activar reward al check‑in con QR y push geolocalizado.',
      changeHistory: [
        { date: '2026-01-20', action: 'Nuevo reward', detail: 'Late Check‑out Wellness' },
        { date: '2026-01-05', action: 'Integración', detail: 'QR check‑in habilitado' }
      ],
      activityLog: [
        { date: '2026-02-04', event: 'Cross‑sell', detail: 'Hotel + Spa · +5.1% lift' },
        { date: '2026-02-01', event: 'Adopción terceros', detail: 'Caldea → Hotel · 18% uso' }
      ]
    },
    'Gym Anytime Andorra': {
      sharedRewards: ['Recovery Day', 'Thermal Recovery'],
      crossSellRate: 14,
      externalAdoption: 19,
      effectivenessScore: 81,
      strategy: 'Segmento fitness con recompensa post‑entreno (19:00–21:00).',
      changeHistory: [
        { date: '2026-01-18', action: 'Optimización horario', detail: 'Ventana 19:00–21:00' },
        { date: '2026-01-02', action: 'Reward compartido', detail: 'Thermal Recovery activo' }
      ],
      activityLog: [
        { date: '2026-02-03', event: 'Redención', detail: 'Recovery Day · 64 usos' },
        { date: '2026-01-31', event: 'Mensajería', detail: 'Open rate 58% segmento fitness' }
      ]
    }
  };

  const defaultMetrics = {
    sharedRewards: ['Wellness Pass', 'Spa Experience'],
    crossSellRate: 10,
    externalAdoption: 12,
    effectivenessScore: 72,
    strategy: 'Optimizar ventanas de envío y test A/B por segmento.',
    changeHistory: [
      { date: '2026-01-15', action: 'Alta de alianza', detail: 'Configuración inicial' }
    ],
    activityLog: [
      { date: '2026-02-01', event: 'Actividad', detail: 'Sincronización de catálogo' }
    ]
  };

  const allianceRows = list.map((partner) => {
    const metrics = allianceMetricsByName[partner.business_name] || defaultMetrics;
    return {
      ...partner,
      ...metrics
    };
  });

  const rewardCatalog = [
    'Thermal Premium',
    'Family Pack',
    'Après‑Ski Relax',
    'Ski & Spa Combo',
    'VIP Thermal Pass',
    'Late Check‑out Wellness',
    'Recovery Day',
    'Thermal Recovery'
  ];

  const filterTimelineItems = (items) => {
    const now = new Date();
    let cutoff = null;
    if (timelineRange === '7d') cutoff = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    if (timelineRange === '30d') cutoff = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    if (timelineRange === '90d') cutoff = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
    return items.filter((i) => {
      const date = new Date(i.date);
      const inRange = cutoff ? date >= cutoff : true;
      const inType = timelineType === 'all' ? true : i.kind === timelineType;
      return inRange && inType;
    }).sort((a, b) => (a.date < b.date ? 1 : -1));
  };

  const summaryCards = [
    {
      title: 'Alianzas Activas',
      value: `${list.filter(p => p.status === 'ACTIVE').length}`,
      subtitle: 'Socios con campañas activas',
      icon: <Loyalty />,
      sparkline: [8, 9, 10, 12, 11, 13, 14],
      breakdown: [
        `Activas: ${list.filter(p => p.status === 'ACTIVE').length}`,
        `En revisión: ${list.filter(p => p.status === 'SUSPENDED').length}`,
        `Inactivas: ${list.filter(p => p.status === 'INACTIVE').length}`,
        'Ciclo de alianza: 6–12 meses promedio',
      ]
    },
    {
      title: 'Rewards Compartidos',
      value: '38',
      subtitle: 'Catálogo inter‑marca',
      icon: <SwapHoriz />,
      sparkline: [30, 31, 33, 34, 36, 37, 38],
      breakdown: [
        'Thermal Premium: 8 acuerdos',
        'Family Pack: 6 acuerdos',
        'Après‑Ski Relax: 5 acuerdos',
        'Otros rewards: 19 acuerdos',
      ]
    },
    {
      title: 'Cross‑sell promedio',
      value: '16.2%',
      subtitle: '+3.1 pp vs Q anterior',
      icon: <TrendingUp />,
      sparkline: [12.4, 13.1, 14.0, 14.8, 15.3, 15.9, 16.2],
      breakdown: [
        'Ski & Spa: 18.4%',
        'Hoteles: 21.0%',
        'Fitness: 14.2%',
        'Retail: 11.3%',
      ]
    },
    {
      title: 'Efectividad PEpL',
      value: '83/100',
      subtitle: 'Impacto en alianzas',
      icon: <Insights />,
      sparkline: [74, 76, 78, 80, 81, 82, 83],
      breakdown: [
        'Lift de adopción rewards: +12.1%',
        'SmartTiming uplift: +6.4%',
        'Redención real: 18.7%',
        'Satisfacción socios: 4.6/5',
      ]
    }
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.75rem', mb: 0.5 }}>
              Marcas Aliadas
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              Gestión estratégica de alianzas, rewards compartidos y cross‑sell con enfoque PEpL.
            </Typography>
          </Box>
          <Button 
            variant="contained" 
            startIcon={<Add />} 
            onClick={handleOpenDialog} 
            sx={{ 
              background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
              boxShadow: '0 4px 14px rgba(14,165,233,0.35)',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Nueva Marca
          </Button>
        </Box>
        <Box sx={{ p: 2, background: 'linear-gradient(135deg, rgba(14,165,233,0.08) 0%, rgba(14,165,233,0.02) 100%)', borderRadius: 2, border: '1px solid rgba(14,165,233,0.15)' }}>
          <Typography variant="body2" sx={{ color: '#0284c7', fontSize: '0.85rem' }}>
            Indicadores de alianza: rewards compartidos, cross‑selling, adopción de rewards de terceros por clientes Caldea y efectividad del proceso PEpL.
          </Typography>
        </Box>
      </Box>

      {/* Filtros */}
      <Card sx={{ 
        mb: 3, 
        borderRadius: 3, 
        border: '1px solid rgba(0,0,0,0.06)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
      }}>
        <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', py: 2 }}>
          <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.8rem' }}>Filtros</Typography>
          <TextField
            select
            size="small"
            label="Periodo"
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            sx={{ 
              minWidth: 160,
              '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.85rem' }
            }}
          >
            <MenuItem value="7d">Últimos 7 días</MenuItem>
            <MenuItem value="30d">Últimos 30 días</MenuItem>
            <MenuItem value="90d">Últimos 90 días</MenuItem>
            <MenuItem value="ytd">Año en curso</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label="Tipo de alianza"
            value={partnerType}
            onChange={(e) => setPartnerType(e.target.value)}
            sx={{ 
              minWidth: 180,
              '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.85rem' }
            }}
          >
            <MenuItem value="all">Todas</MenuItem>
            <MenuItem value="hotel">Hoteles</MenuItem>
            <MenuItem value="ski">Esquí</MenuItem>
            <MenuItem value="fitness">Fitness</MenuItem>
            <MenuItem value="retail">Retail</MenuItem>
          </TextField>
          <Chip 
            label="Aplicado" 
            size="small" 
            sx={{ bgcolor: 'rgba(14,165,233,0.1)', color: '#0284c7', fontWeight: 500 }} 
          />
        </CardContent>
      </Card>

      {/* Summary Cards (Drill‑down) */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {summaryCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.title}>
            <Card sx={{ 
              height: '100%',
              borderRadius: 3, 
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              transition: 'all 0.2s ease',
              '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.08)', transform: 'translateY(-2px)' }
            }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem', mb: 0.5 }}>{card.title}</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1.75rem' }}>{card.value}</Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>{card.subtitle}</Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'rgba(14,165,233,0.1)', color: '#0284c7', width: 40, height: 40 }}>{card.icon}</Avatar>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                  <Sparkline data={card.sparkline} />
                  <Chip label={timeRange.toUpperCase()} size="small" sx={{ bgcolor: 'rgba(0,0,0,0.04)', color: '#64748b', fontSize: '0.65rem' }} />
                </Box>
                <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, mt: 2, bgcolor: 'transparent' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />} sx={{ p: 0, minHeight: 'auto', '& .MuiAccordionSummary-content': { m: 0 } }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#0ea5e9', fontSize: '0.7rem' }}>Ver composición</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 1, px: 0 }}>
                    <List dense sx={{ py: 0 }}>
                      {card.breakdown.map((line) => (
                        <ListItem key={line} sx={{ py: 0.25, px: 0 }}>
                          <ListItemText primary={line} primaryTypographyProps={{ fontSize: '0.75rem', color: '#64748b' }} />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {error && <Box sx={{ color: 'error.main', mb: 2 }}>{error}</Box>}

      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Gestión de Alianzas & Rewards Compartidos</Typography>
            <Chip label="PEpL Alliance Intelligence" color="primary" size="small" />
          </Box>
          <Divider sx={{ mb: 2 }} />
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell><strong>Marca / Entidad</strong></TableCell>
                  <TableCell><strong>Rewards Compartidos</strong></TableCell>
                  <TableCell><strong>Cross‑sell</strong></TableCell>
                  <TableCell><strong>Adopción Rewards Terceros</strong></TableCell>
                  <TableCell><strong>Efectividad PEpL</strong></TableCell>
                  <TableCell><strong>Estado</strong></TableCell>
                  <TableCell><strong>Acciones</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allianceRows.map((partner) => {
                  const assigned = assignedRewards[partner.id] || partner.sharedRewards;
                  return (
                  <TableRow key={partner.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }}>
                          {partner.business_name?.[0] || 'A'}
                        </Avatar>
                        <Box>
                          <Typography sx={{ fontWeight: 600 }}>{partner.business_name}</Typography>
                          <Typography variant="caption" color="textSecondary">{partner.city || 'Andorra'}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {assigned.slice(0, 2).map((r) => (
                          <Chip key={r} label={r} size="small" variant="outlined" />
                        ))}
                        {assigned.length > 2 && (
                          <Chip label={`+${assigned.length - 2}`} size="small" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600 }}>{partner.crossSellRate}%</Typography>
                      <LinearProgress variant="determinate" value={partner.crossSellRate} sx={{ height: 6, borderRadius: 4, mt: 0.5 }} />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600 }}>{partner.externalAdoption}%</Typography>
                      <LinearProgress variant="determinate" value={partner.externalAdoption} sx={{ height: 6, borderRadius: 4, mt: 0.5 }} color="success" />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600 }}>{partner.effectivenessScore}/100</Typography>
                      <LinearProgress variant="determinate" value={partner.effectivenessScore} sx={{ height: 6, borderRadius: 4, mt: 0.5 }} color="secondary" />
                      <Typography variant="caption" color="textSecondary">{partner.strategy}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={partner.status} color={getStatusColor(partner.status)} size="small" />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Ver detalle">
                        <Button size="small" startIcon={<Visibility />} onClick={() => handleView(partner)} />
                      </Tooltip>
                      <Tooltip title="Asignar rewards">
                        <Button size="small" startIcon={<SwapHoriz />} onClick={() => handleOpenAssign(partner)} />
                      </Tooltip>
                      <Tooltip title="Editar">
                        <Button size="small" startIcon={<Edit />} onClick={() => handleEdit(partner)} />
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <Button size="small" startIcon={<Delete />} color="error" onClick={() => handleDelete(partner.id)} />
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog: Detalle de alianza */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Detalle de Alianza</DialogTitle>
        <DialogContent>
          {activePartner && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }}>
                  {activePartner.business_name?.[0] || 'A'}
                </Avatar>
                <Box>
                  <Typography variant="h6">{activePartner.business_name}</Typography>
                  <Typography variant="caption" color="textSecondary">{activePartner.city || 'Andorra'} · {activePartner.email}</Typography>
                </Box>
                <Chip label={activePartner.status} color={getStatusColor(activePartner.status)} size="small" />
              </Box>

              <Divider />

              <Typography variant="subtitle2">Rewards asignados (intercambio)</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {(assignedRewards[activePartner.id] || activePartner.sharedRewards || []).map((r) => (
                  <Chip key={r} label={r} size="small" />
                ))}
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="caption" color="textSecondary">Cross‑sell</Typography>
                      <Typography variant="h6">{activePartner.crossSellRate}%</Typography>
                      <LinearProgress variant="determinate" value={activePartner.crossSellRate} sx={{ mt: 1 }} />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="caption" color="textSecondary">Adopción Rewards Terceros</Typography>
                      <Typography variant="h6">{activePartner.externalAdoption}%</Typography>
                      <LinearProgress variant="determinate" value={activePartner.externalAdoption} sx={{ mt: 1 }} color="success" />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="caption" color="textSecondary">Efectividad PEpL</Typography>
                      <Typography variant="h6">{activePartner.effectivenessScore}/100</Typography>
                      <LinearProgress variant="determinate" value={activePartner.effectivenessScore} sx={{ mt: 1 }} color="secondary" />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8fafc' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Estrategia propuesta (mejora continua)</Typography>
                <Typography variant="body2" color="textSecondary">{activePartner.strategy}</Typography>
              </Paper>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Historial de cambios</Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Fecha</TableCell>
                            <TableCell>Acción</TableCell>
                            <TableCell>Detalle</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(activePartner.changeHistory || []).map((row, idx) => (
                            <TableRow key={`${row.date}-${idx}`}>
                              <TableCell>{row.date}</TableCell>
                              <TableCell>{row.action}</TableCell>
                              <TableCell>{row.detail}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Log de actividad</Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Fecha</TableCell>
                            <TableCell>Evento</TableCell>
                            <TableCell>Detalle</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(activePartner.activityLog || []).map((row, idx) => (
                            <TableRow key={`${row.date}-${idx}`}> 
                              <TableCell>{row.date}</TableCell>
                              <TableCell>{row.event}</TableCell>
                              <TableCell>{row.detail}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Timeline visual</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <TextField
                        select
                        size="small"
                        label="Tipo"
                        value={timelineType}
                        onChange={(e) => setTimelineType(e.target.value)}
                        sx={{ minWidth: 140 }}
                      >
                        <MenuItem value="all">Todo</MenuItem>
                        <MenuItem value="change">Cambios</MenuItem>
                        <MenuItem value="activity">Actividad</MenuItem>
                      </TextField>
                      <TextField
                        select
                        size="small"
                        label="Rango"
                        value={timelineRange}
                        onChange={(e) => setTimelineRange(e.target.value)}
                        sx={{ minWidth: 140 }}
                      >
                        <MenuItem value="7d">Últimos 7 días</MenuItem>
                        <MenuItem value="30d">Últimos 30 días</MenuItem>
                        <MenuItem value="90d">Últimos 90 días</MenuItem>
                        <MenuItem value="all">Todo</MenuItem>
                      </TextField>
                    </Box>
                  </Box>
                  <Timeline
                    items={filterTimelineItems([
                      ...(activePartner.changeHistory || []).map((c) => ({
                        kind: 'change',
                        date: c.date,
                        title: c.action,
                        detail: c.detail
                      })),
                      ...(activePartner.activityLog || []).map((a) => ({
                        kind: 'activity',
                        date: a.date,
                        title: a.event,
                        detail: a.detail
                      }))
                    ])}
                  />
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Cerrar</Button>
          {activePartner && (
            <Button variant="contained" onClick={() => handleOpenAssign(activePartner)}>
              Asignar Rewards
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog: Asignación de Rewards */}
      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Asignar Rewards a la Alianza</DialogTitle>
        <DialogContent>
          {activePartner && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {activePartner.business_name} · Selección de rewards compartidos
              </Typography>
              <Grid container spacing={1}>
                {rewardCatalog.map((reward) => (
                  <Grid item xs={12} sm={6} key={reward}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={rewardSelection.includes(reward)}
                          onChange={() => handleToggleReward(reward)}
                        />
                      }
                      label={reward}
                    />
                  </Grid>
                ))}
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="textSecondary">
                El intercambio de rewards se contabiliza en la ventana post‑reward y afecta cross‑sell y adopción de terceros.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveAssign}>Guardar asignación</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Editar Marca' : 'Nueva Marca'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Nombre Comercial"
              name="businessName"
              value={formik.values.businessName}
              onChange={formik.handleChange}
              error={formik.touched.businessName && Boolean(formik.errors.businessName)}
              helperText={formik.touched.businessName && formik.errors.businessName}
            />
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
            <TextField
              fullWidth
              label="Teléfono"
              name="phone"
              value={formik.values.phone}
              onChange={formik.handleChange}
              error={formik.touched.phone && Boolean(formik.errors.phone)}
              helperText={formik.touched.phone && formik.errors.phone}
            />
            <TextField
              fullWidth
              label="Dirección"
              name="address"
              value={formik.values.address}
              onChange={formik.handleChange}
            />
            <TextField
              fullWidth
              label="Ciudad"
              name="city"
              value={formik.values.city}
              onChange={formik.handleChange}
            />
            <TextField
              fullWidth
              label="País"
              name="country"
              value={formik.values.country}
              onChange={formik.handleChange}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={formik.handleSubmit} variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
